import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDemo } from "@/store/DemoContext";
import { buildSeasonRows } from "@/lib/standings";
import { seasonScore, scoreMeta } from "@/lib/scoring";
import { fmtScore } from "@/lib/format";
import { Crest } from "@/components/Crest";
import { ENTITY } from "@/data/constants";
import { Cpu, ClipboardList } from "lucide-react";

const C = { you: ENTITY.you.color, coach: ENTITY.coach.color, ai: ENTITY.ai.color };

export default function Leaderboard() {
  const { t } = useTranslation();
  const { dataset, state } = useDemo();
  const { language, scoringMode } = state;
  const profile = dataset.profiles[state.profileId];
  const meta = scoreMeta(scoringMode);

  const [scope, setScope] = useState("season"); // season | matchday
  const [region, setRegion] = useState("global"); // global | league

  // reference history for benchmark numbers (fall back to mid if new user empty)
  const refHistory = profile.history.length ? profile.history : dataset.profiles.mid.history;
  const matchday = scope === "matchday" ? refHistory[refHistory.length - 1]?.matchday : null;

  const leagueId = region === "league" ? profile.favClubId.split("-")[0] : null;
  const { sorted, unrankedYou } = useMemo(
    () => buildSeasonRows(scoringMode, dataset, profile, { leagueId, matchday }),
    [scoringMode, dataset, profile, leagueId, matchday],
  );

  const benchHistory = scope === "matchday" ? refHistory.filter((h) => h.matchday === matchday) : refHistory;
  const aiScore = seasonScore(scoringMode, benchHistory.map((h) => ({ lgd: h.aiLGD, actualGD: h.actualGD, coachLGD: h.coachLGD })));
  const coachScore = seasonScore(scoringMode, benchHistory.map((h) => ({ lgd: h.coachLGD, actualGD: h.actualGD, coachLGD: h.coachLGD })));
  const coachWins = benchHistory.length;

  const scopeOptions = useMemo(() => [["matchday", t("leaderboard.thisMatchday")], ["season", t("leaderboard.thisSeason")]], [t]);
  const regionOptions = useMemo(() => [["global", t("leaderboard.global")], ["league", t("leaderboard.perLeague")]], [t]);

  return (
    <div className="max-w-4xl mx-auto px-3 sm:px-6 py-5 space-y-4">
      <h1 className="font-display text-3xl font-bold uppercase tracking-wide">{t("leaderboard.title")}</h1>

      <div className="flex flex-wrap gap-2">
        <ToggleGroup value={scope} onChange={setScope} options={scopeOptions} testid="lb-scope" />
        <ToggleGroup value={region} onChange={setRegion} options={regionOptions} testid="lb-region" />
      </div>

      <div className="rounded-2xl border border-neutral-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase text-neutral-500 border-b border-neutral-800 bg-neutral-900/60">
              <th className="text-left px-3 py-2 font-display w-10">{t("leaderboard.rank")}</th>
              <th className="text-left px-2 py-2 font-display">{t("leaderboard.name")}</th>
              <th className="text-right px-2 py-2 font-display">{t("leaderboard.score")}</th>
              <th className="text-right px-2 py-2 font-display hidden sm:table-cell">{t("leaderboard.played")}</th>
              <th className="text-right px-3 py-2 font-display hidden sm:table-cell">{t("leaderboard.vsCoach")}</th>
            </tr>
          </thead>
          <tbody data-testid="leaderboard-body">
            {/* pinned benchmark rows */}
            <BenchmarkRow icon={<Cpu size={14} />} label={t("common.ai")} color={C.ai} score={fmtScore(scoringMode, aiScore, language)} played={benchHistory.length} vs="—" testid="lb-row-ai" />
            <BenchmarkRow icon={<ClipboardList size={14} />} label={t("common.coach")} color={C.coach} score={fmtScore(scoringMode, coachScore, language)} played={coachWins} vs="—" testid="lb-row-coach" />
            <tr><td colSpan={5} className="h-1 bg-neutral-800/40" /></tr>

            {sorted.map((r) => (
              <Row key={r.id} r={r} dataset={dataset} scoringMode={scoringMode} language={language} t={t} />
            ))}
          </tbody>
        </table>
      </div>

      {/* sticky your-row (or empty state) */}
      {unrankedYou ? (
        <div className="sticky bottom-2 rounded-xl border border-[#b6ff2e]/40 bg-neutral-900/95 backdrop-blur p-4" data-testid="lb-empty-you">
          <div className="font-display uppercase text-sm text-[#b6ff2e]">{t("leaderboard.emptyTitle")}</div>
          <p className="text-xs text-neutral-400 mt-1">{t("leaderboard.emptyBody")}</p>
        </div>
      ) : (
        <StickyYou sorted={sorted} dataset={dataset} scoringMode={scoringMode} language={language} t={t} />
      )}
    </div>
  );
}

function ToggleGroup({ value, onChange, options, testid }) {
  return (
    <div className="flex gap-1 p-1 rounded-xl bg-neutral-900 border border-neutral-800" data-testid={testid}>
      {options.map(([v, label]) => (
        <button key={v} data-testid={`${testid}-${v}`} onClick={() => onChange(v)}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${value === v ? "bg-[#b6ff2e] text-black" : "text-neutral-400 hover:text-neutral-100"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

function BenchmarkRow({ icon, label, color, score, played, vs, testid }) {
  return (
    <tr data-testid={testid} className="border-b border-neutral-900" style={{ background: `${color}0f` }}>
      <td className="px-3 py-2.5"><span className="grid place-items-center w-6 h-6 rounded-md" style={{ background: `${color}22`, color }}>{icon}</span></td>
      <td className="px-2 py-2.5">
        <span className="font-display font-bold uppercase text-sm" style={{ color }}>{label}</span>
        <span className="ml-2 text-[9px] uppercase tracking-wide text-neutral-500 border border-neutral-700 rounded px-1 py-0.5">BENCHMARK</span>
      </td>
      <td className="px-2 py-2.5 text-right font-display font-bold tabular" style={{ color }}>{score}</td>
      <td className="px-2 py-2.5 text-right tabular text-neutral-400 hidden sm:table-cell">{played}</td>
      <td className="px-3 py-2.5 text-right tabular text-neutral-500 hidden sm:table-cell">{vs}</td>
    </tr>
  );
}

function Row({ r, dataset, scoringMode, language, t }) {
  const club = dataset.clubsById[r.favClubId];
  return (
    <tr data-testid={r.isYou ? "lb-row-you" : `lb-row-${r.id}`}
      className={`border-b border-neutral-900 last:border-0 ${r.isYou ? "bg-[#b6ff2e]/10" : "hover:bg-neutral-900/40"}`}>
      <td className="px-3 py-2 font-display font-bold tabular text-neutral-400">{r.rank}</td>
      <td className="px-2 py-2">
        <div className="flex items-center gap-2">
          <Crest club={club} size={22} />
          <span className={`font-medium ${r.isYou ? "text-[#b6ff2e]" : ""}`}>{r.isYou ? t("leaderboard.you") : r.name}</span>
        </div>
      </td>
      <td className="px-2 py-2 text-right font-display font-semibold tabular">{fmtScore(scoringMode, r.score, language)}</td>
      <td className="px-2 py-2 text-right tabular text-neutral-400 hidden sm:table-cell">{r.played}</td>
      <td className="px-3 py-2 text-right tabular text-neutral-400 hidden sm:table-cell">{r.vsCoach}</td>
    </tr>
  );
}

function StickyYou({ sorted, dataset, scoringMode, language, t }) {
  const me = sorted.find((r) => r.isYou);
  if (!me) return null;
  const club = dataset.clubsById[me.favClubId];
  return (
    <div className="sticky bottom-2 rounded-xl border border-[#b6ff2e]/50 bg-neutral-900/95 backdrop-blur px-4 py-3 flex items-center justify-between" data-testid="lb-sticky-you">
      <div className="flex items-center gap-3">
        <span className="font-display font-bold tabular text-[#b6ff2e]">#{me.rank}</span>
        <Crest club={club} size={24} />
        <span className="font-medium text-[#b6ff2e]">{t("leaderboard.you")}</span>
      </div>
      <div className="flex items-center gap-4">
        <span className="font-display font-bold tabular text-[#b6ff2e]">{fmtScore(scoringMode, me.score, language)}</span>
        <span className="text-xs text-neutral-400">{me.vsCoach} {t("leaderboard.vsCoach")}</span>
      </div>
    </div>
  );
}
